import { motion } from 'framer-motion';
import { HiHeart, HiSparkles } from 'react-icons/hi2';
import { GiCrossedSwords, GiCompass } from 'react-icons/gi';
import './UserCard.css';

// ===== TYPES =====
interface SkillData {
  name: string;
  category: string;
  confidence: number; // 0-100
}

interface BadgeData {
  id: string;
  name: string;
  icon: string;
}

interface ArchetypeData {
  name: string;
  icon: string;
}

interface DeckProfile {
  id: string;
  username: string;
  displayName: string;
  level: number;
  mkeyType?: string;
  bio: string | null;
  tagline: string | null;
  avatarUrl: string | null;
  coverUrl: string | null;
  coverGradient?: string;
  storiesCount: number;
  connectionsCount: number;
  isCurrentUser: boolean;
  isSubscriber?: boolean;
  admirationsCount?: number;
  compatibilityScore?: number | null;
  odysseyTitle?: string;
  subscriptionTier?: number;
  createdAt?: number | string;
  role?: string | null;
  // DECK 2.0
  archetype?: ArchetypeData | null;
  hasDna?: boolean;
  topSkills?: SkillData[];
  values?: string[];
  badges?: BadgeData[];
  // VORTEX
  matchType?: string;
  matchReasons?: string[];
  sharedSkills?: string[];
  sharedValues?: string[];
  scores?: { skills: number; values: number; archetypes: number; context: number };
}

interface UserCardProps {
  profile: DeckProfile;
  onClick: () => void;
  onAdmire?: (userId: string) => void;
  onConnect?: (userId: string) => void;
  isAdmired?: boolean;
  connectionStatus?: string;
  showVortexData?: boolean;
}

// ===== RANK SYSTEM =====
function getRankTier(level: number): { tier: string; color: string; name: string } {
  if (level >= 60) return { tier: 'nobre', color: '#FFD700', name: 'Nobre' };
  if (level >= 50) return { tier: 'almirante', color: '#EF4444', name: 'Almirante' };
  if (level >= 40) return { tier: 'capitao', color: '#F59E0B', name: 'Capitao' };
  if (level >= 30) return { tier: 'timoneiro', color: '#34D399', name: 'Timoneiro' };
  if (level >= 20) return { tier: 'marujo', color: '#06B6D4', name: 'Marujo' };
  if (level >= 10) return { tier: 'aspirante', color: '#3B82F6', name: 'Aspirante' };
  return { tier: 'recruta', color: '#9CA3AF', name: 'Recruta' };
}

// ===== ARCHETYPE COLORS =====
const ARCHETYPE_COLORS: Record<string, string> = {
  'heroi': '#DC2626', 'hero': '#DC2626', 'herói': '#DC2626',
  'criador': '#7C3AED', 'creator': '#7C3AED',
  'explorador': '#059669', 'explorer': '#059669',
  'sabio': '#2563EB', 'sage': '#2563EB', 'sábio': '#2563EB',
  'governante': '#D4AF37', 'ruler': '#D4AF37',
  'cuidador': '#EC4899', 'caregiver': '#EC4899',
  'mago': '#4F46E5', 'magician': '#4F46E5',
  'rebelde': '#EA580C', 'rebel': '#EA580C', 'outlaw': '#EA580C',
  'amante': '#E11D48', 'lover': '#E11D48',
  'bobo': '#EAB308', 'jester': '#EAB308', 'bobo da corte': '#EAB308',
  'inocente': '#06B6D4', 'innocent': '#06B6D4',
  'cara comum': '#94A3B8', 'everyman': '#94A3B8', 'regular': '#94A3B8',
};

// ===== ARCHETYPE ICONS (SVG from react-icons) =====
const ARCHETYPE_ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
  'heroi': GiCrossedSwords, 'hero': GiCrossedSwords, 'herói': GiCrossedSwords,
  'explorador': GiCompass, 'explorer': GiCompass,
  'governante': GiCrossedSwords, 'ruler': GiCrossedSwords,
};

// ===== MATCH TYPE CONFIG (SVG icons instead of emojis) =====
const MATCH_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  aliado: { label: 'ALIADO', color: '#D4AF37' },
  espelho: { label: 'ESPELHO', color: '#94A3B8' },
  mentor: { label: 'MENTOR', color: '#2563EB' },
  aprendiz: { label: 'APRENDIZ', color: '#059669' },
  parceiro: { label: 'PARCEIRO', color: '#7C3AED' },
};

// ===== SVG ICONS =====
const IconVerified = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307zm7.007 6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
  </svg>
);

function getArchetypeColor(archetype: ArchetypeData | null | undefined): string {
  if (!archetype) return '#60a5fa';
  return ARCHETYPE_COLORS[archetype.name.toLowerCase().trim()] || '#60a5fa';
}

export function UserCard({
  profile,
  onClick,
  onAdmire,
  onConnect,
  isAdmired = false,
  showVortexData = false,
}: UserCardProps) {
  const rank = getRankTier(profile.level);
  const archColor = getArchetypeColor(profile.archetype);
  const matchType = profile.matchType ? MATCH_TYPE_CONFIG[profile.matchType] : null;
  const hasDna = profile.hasDna ?? false;
  const odysseyTitle = profile.odysseyTitle || '';
  const ArchIcon = profile.archetype
    ? ARCHETYPE_ICONS[profile.archetype.name.toLowerCase().trim()]
    : null;

  const getInitials = () => {
    if (!profile.displayName) return '?';
    const names = profile.displayName.split(' ');
    if (names.length >= 2) return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    return profile.displayName[0].toUpperCase();
  };

  return (
    <motion.div
      className="card-aaa"
      data-tier={rank.tier}
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      whileHover={{ y: -6 }}
      style={{
        '--rank-color': rank.color,
        '--rank-glow': `${rank.color}33`,
        '--arch-color': archColor,
      } as React.CSSProperties}
    >
      {/* ===== ZONE 1: COVER ===== */}
      <div className="card-cover">
        <div
          className="card-cover-img"
          style={
            profile.coverUrl
              ? { backgroundImage: `url(${profile.coverUrl})` }
              : profile.coverGradient
              ? { background: profile.coverGradient }
              : undefined
          }
        />
        <div className="card-cover-fade" />

        {/* Level badge */}
        <span className="card-level-badge">
          Lv.{profile.level}
        </span>

        {/* Match type badge (only in VORTEX results) */}
        {matchType && showVortexData && (
          <span className="card-match-badge" style={{ backgroundColor: matchType.color }}>
            {matchType.label}
          </span>
        )}

        {/* "Voce" badge */}
        {profile.isCurrentUser && (
          <span className="card-you-badge">Voce</span>
        )}
      </div>

      {/* ===== ZONE 2: IDENTITY ===== */}
      <div className="card-identity">
        <div className="card-avatar-wrap">
          <div className="card-avatar-ring" />
          <div className="card-avatar">
            {profile.avatarUrl ? (
              <img src={profile.avatarUrl} alt={profile.displayName} />
            ) : (
              <span className="card-initials">{getInitials()}</span>
            )}
          </div>
        </div>

        <div className="card-name-block">
          <div className="card-name-row">
            <h3 className="card-name">{profile.displayName}</h3>
            {profile.isSubscriber && (
              <span className="card-verified"><IconVerified /></span>
            )}
          </div>
          <span className="card-username">@{profile.username}</span>
          {odysseyTitle && (
            <span className="card-odyssey-title">{odysseyTitle}</span>
          )}
        </div>
      </div>

      {/* ===== ZONE 3: DNA SNAPSHOT ===== */}
      {hasDna && (
        <div className="card-dna">
          {/* Archetype */}
          {profile.archetype && (
            <div className="card-archetype">
              <span className="card-archetype-icon" style={{ color: archColor }}>
                {ArchIcon ? <ArchIcon size={16} /> : <GiCompass size={16} />}
              </span>
              <span className="card-archetype-name" style={{ color: archColor }}>
                {profile.archetype.name}
              </span>
            </div>
          )}

          {/* Top 3 Skills as pure numbers */}
          {profile.topSkills && profile.topSkills.length > 0 && (
            <div className="card-skills">
              {profile.topSkills.slice(0, 3).map((skill, i) => (
                <div key={i} className="card-skill-row">
                  <span className="card-skill-name">{skill.name}</span>
                  <span className="card-skill-score">{skill.confidence}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== ZONE 4: ACTIONS ===== */}
      {!profile.isCurrentUser && (
        <div className="card-actions">
          <button
            className={`card-btn card-btn-admire ${isAdmired ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); onAdmire?.(profile.id); }}
            title={isAdmired ? 'Remover admiracao' : 'Admirar'}
          >
            <HiHeart size={15} />
            <span>Admirar</span>
          </button>
          <button
            className="card-btn card-btn-match"
            onClick={(e) => { e.stopPropagation(); onConnect?.(profile.id); }}
          >
            <HiSparkles size={15} />
            <span>Match</span>
          </button>
        </div>
      )}
    </motion.div>
  );
}
